import React from 'react'
import { useTranslation } from 'react-i18next'
import styles from './styles/player-search-bar.module.scss'
import { formatAddress, getAgeReference } from 'utils'
import Avatar from 'components/profile/Avatar'
import WatchlistBadge from 'components/watchlist/WatchlistBadge'
import cx from 'classnames'
import { Player } from 'types/global'
import { useCountries } from 'hooks/use-countries'
import ConfidenceTick from 'components/generic/ConfidenceTick'

const chainInfo = (info: (string | undefined)[], separator: string = ', ') => {
  return info.reduce((acc, data, i) => {
    if (acc && data) return acc + separator + data

    return data || acc
  }, '')
}

interface Props {
  player: Player
  ariaLabel?: string
  onClick?: () => void
  showWatchlistStatus?: boolean
  isInWatchlists?: boolean
  className?: string
}

const PlayerSuggestion = ({
  player,
  onClick,
  showWatchlistStatus = false,
  isInWatchlists = false,
  className = '',
}: Props) => {
  const { t: tData } = useTranslation('strData')
  const {
    firstName,
    lastName,
    age,
    birthYear,
    countryCode,
    avatar,
    location,
    gender,
    wtnSingles,
    wtnConfidenceSingles,
  } = player
  const { getCountryName } = useCountries()
  const formattedLocation = location?.main && formatAddress(location.main)
  const mainInfo = chainInfo([
    gender?.charAt(0),
    getAgeReference(age, birthYear),
    getCountryName(countryCode),
  ])
  const wtnNumberAndConfidence = wtnSingles ? (
    <span className={styles.wtn}>
      {wtnSingles}
      <ConfidenceTick score={wtnConfidenceSingles} className={styles.confidence} />
    </span>
  ) : null

  return (
    <article
      className={cx(styles.playerSuggestionWrapper, className)}
      onClick={() => onClick && onClick()}
      role={onClick ? 'button' : undefined}
    >
      <div
        className={cx(styles.avatarContainer, { [styles.withExtraInfo]: !!formattedLocation })}
        aria-hidden
      >
        <Avatar url={avatar} size='xs' />
      </div>

      <section className={styles.suggestionInfo}>
        <header className={styles.header}>
          <h3 className={styles.name}>
            {firstName && lastName && (
              <>
                {firstName}{' '}
                <em className={styles.suggestionSurname}>
                  {lastName} {wtnNumberAndConfidence}
                </em>
              </>
            )}
            {!firstName && !lastName && (
              <>
                {tData('noPlayerName')}
                {wtnNumberAndConfidence}
              </>
            )}
          </h3>
        </header>
        <p className={styles.info}>{mainInfo}</p>
        {formattedLocation && <p className={styles.info}>{formattedLocation}</p>}
      </section>

      {showWatchlistStatus && (
        <span className={styles.wlBadge}>
          <WatchlistBadge isMember={isInWatchlists} />
        </span>
      )}
    </article>
  )
}

export default PlayerSuggestion
